import { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, X, FileText, Image, File, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = [
  'application/pdf',
  'text/plain',
  'text/csv',
  'application/json',
  'image/png',
  'image/jpeg',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const ALLOWED_EXTENSIONS = ['.pdf', '.txt', '.csv', '.json', '.png', '.jpg', '.jpeg', '.webp', '.doc', '.docx'];

function getFileIcon(type) {
  if (type.startsWith('image/')) return Image;
  if (type.includes('pdf') || type.includes('word')) return FileText;
  return File;
}

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

export function FileUpload({ onFilesChange, maxFiles = 5, className = '' }) {
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const validateFile = (file) => {
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`${file.name} is too large. Max size is 10MB.`);
      return false;
    }
    
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      toast.error(`${file.name} has an unsupported format.`);
      return false;
    }
    
    return true;
  };

  const handleFiles = useCallback((newFiles) => {
    const validFiles = Array.from(newFiles).filter(validateFile);
    
    if (files.length + validFiles.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} files allowed`);
      return;
    }

    // Create preview for images
    const filesWithPreview = validFiles.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
    }));

    const updated = [...files, ...filesWithPreview];
    setFiles(updated);
    onFilesChange?.(updated.map(f => f.file));
  }, [files, maxFiles, onFilesChange]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const removeFile = (id) => {
    const file = files.find(f => f.id === id);
    if (file?.preview) {
      URL.revokeObjectURL(file.preview);
    }
    const updated = files.filter(f => f.id !== id);
    setFiles(updated);
    onFilesChange?.(updated.map(f => f.file));
  };

  return (
    <div className={className}>
      {/* Drop Zone */}
      <motion.div
        onClick={() => inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        animate={{
          borderColor: isDragging ? 'rgba(20, 184, 166, 0.5)' : 'rgba(255, 255, 255, 0.1)',
          backgroundColor: isDragging ? 'rgba(20, 184, 166, 0.05)' : 'transparent',
        }}
        className="relative border-2 border-dashed rounded-xl p-6 cursor-pointer transition-colors hover:border-teal-500/30 hover:bg-teal-500/5"
        data-testid="file-upload-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ALLOWED_EXTENSIONS.join(',')}
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
          data-testid="file-upload-input"
        />
        
        <div className="flex flex-col items-center text-center">
          <div className="w-12 h-12 rounded-full bg-teal-500/10 flex items-center justify-center mb-3">
            <Upload className="w-6 h-6 text-teal-400" />
          </div>
          <p className="text-sm text-zinc-300 mb-1">
            <span className="text-teal-400 font-medium">Click to upload</span> or drag and drop
          </p>
          <p className="text-xs text-zinc-500">
            PDF, TXT, CSV, JSON, Images • Max 10MB each
          </p>
        </div>
      </motion.div>

      {/* File List */}
      <AnimatePresence>
        {files.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 space-y-2"
          >
            {files.map((item) => {
              const Icon = getFileIcon(item.file.type);
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="flex items-center gap-3 bg-zinc-800/50 rounded-lg p-3 group"
                  data-testid={`uploaded-file-${item.id}`}
                >
                  {/* Preview or Icon */}
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-zinc-700 flex items-center justify-center flex-shrink-0">
                    {item.preview ? (
                      <img src={item.preview} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Icon className="w-5 h-5 text-zinc-400" />
                    )}
                  </div>
                  
                  {/* File Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{item.file.name}</p>
                    <p className="text-xs text-zinc-500">{formatFileSize(item.file.size)}</p>
                  </div>
                  
                  {/* Remove Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(item.id);
                    }}
                    className="w-8 h-8 rounded-full flex items-center justify-center text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors opacity-0 group-hover:opacity-100"
                    data-testid={`remove-file-${item.id}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* File Count */}
      {files.length > 0 && (
        <p className="text-xs text-zinc-500 mt-2">
          {files.length} of {maxFiles} files uploaded
        </p>
      )}
    </div>
  );
}

export default FileUpload;
